import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { useAuth } from '../../AuthContext';
import ClassDetails from '../Classes/ClassDetails';
import './ClassScheduleTab.css';
import configData from '../../configure';


// Accent colors for schedule blocks
const accentColors = [
  { bg: "#eaf2fc", border: "#3984ec", time: "#3984ec" },
  // { bg: "#fffbe6", border: "#dab319", time: "#dab319" },
  { bg: "#e4fff5", border: "#34bc7c", time: "#34bc7c" },
  // { bg: "#ffeaea", border: "#eb5757", time: "#eb5757" },
  // { bg: "#f1eaff", border: "#6c39ec", time: "#6c39ec" },
];

function getAccent(i) {
  return accentColors[i % accentColors.length];
}

function timeRange(startTime, durationMins) {
  const [h, m] = startTime.split(":").map(Number);
  const start = new Date(2000, 1, 1, h, m);
  const end = new Date(start.getTime() + durationMins * 60000);
  const fmt = (d) =>
    d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })
     .replace(/^24:/, '00:');
  return fmt(start) + "–" + fmt(end);
}

const dayOrder = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const ClassScheduleTab = ({ selectedUser ,facultyInfo}) => {
  const [classesData, setClassesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { axios, token } = useAuth();

const userId = selectedUser.rawData?.user?.userId || facultyInfo?.userId || 'unknown';

 useEffect(() => {
  if (!userId) {
    toast.error('No user ID provided');
    setLoading(false);
    return;
  }
  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch course plans using the correct endpoint
      const plansRes = await axios.get(
        `${configData.COURSE_PLAN}/filter?filterType=faculty&id=${userId}`,
        { headers: { Accept: 'application/json', Authorization: token } }
      );
      const coursePlans = plansRes.data;
      const classIds = [...new Set(coursePlans.map(p => p.classId))];
      const courseIds = [...new Set(coursePlans.map(p => p.courseId))];

      // Rest of the code remains unchanged
      const courseDetailsCache = {};
      const courseDetailsPromises = courseIds.map(async id => {
        try {
          const res = await axios.get(
            `${configData.COURSE_BY_ID}/${id}`,
            { headers: { Accept: 'application/json', Authorization: token } }
          );
          courseDetailsCache[id] = res.data;
        } catch {
          courseDetailsCache[id] = null;
        }
      });
      await Promise.all(courseDetailsPromises);

      const classesPromises = classIds.map(async classId => {
        const res = await axios.get(
          `${configData.CLASS_BY_ID}/${classId}`,
          { headers: { Accept: 'application/json', Authorization: token } }
        );
        const classData = res.data;

        const facultySchedule = {};
        Object.entries(classData.classScheduleMap || {}).forEach(([day, sessions]) => {
          const filtered = sessions.filter(s => s.facultyId === userId);
          if (filtered.length) facultySchedule[day] = filtered;
        });

        return {
          id: classId,
          className: classData.className,
          sectionName: classData.sectionName,
          facultySchedule,
        };
      });

      const classes = await Promise.all(classesPromises);
      setClassesData(classes);
      setLoading(false);
    } catch (e) {
      toast.error("Failed fetching class data");
      setLoading(false);
      console.error(e);
    }
  };
  fetchData();
}, [userId, axios, token]);

  // Merge by day
  const mergedScheduleByDay = {};
  classesData.forEach(cls => {
    Object.entries(cls.facultySchedule || {}).forEach(([day, sessions]) => {
      if (!mergedScheduleByDay[day]) mergedScheduleByDay[day] = [];
      sessions.forEach(session => {
        mergedScheduleByDay[day].push({
          ...session,
          className: cls.className,
          sectionName: cls.sectionName,
        });
      });
    });
  });

  Object.values(mergedScheduleByDay).forEach(sessions =>
    sessions.sort((a, b) => a.startTime.localeCompare(b.startTime))
  );

  const sortedDays = dayOrder.filter(d => mergedScheduleByDay[d] && mergedScheduleByDay[d].length > 0);

  if (loading) return <Typography>Loading schedule...</Typography>;

  return (
    <Box className="scheduleContainer">
      <Typography variant="h6" className="scheduleTitle">
        Full Schedule
      </Typography>

      {sortedDays.length === 0 ? (
        <Typography color="textSecondary" className="noScheduleText">
          No scheduled classes found.
        </Typography>
      ) : (
        sortedDays.map((dayName) => (
          <Box key={dayName} className="daySection">
            <Typography variant="h6" className="dayHeader">
              {dayName}
            </Typography>
            {mergedScheduleByDay[dayName].map((s, idx) => {
              const accent = getAccent(idx);
              return (
                <Box
                  key={idx}
                  className="scheduleCard"
                  style={{
                    background: accent.bg,
                    borderLeft: `8px solid ${accent.border}`,
                  }}
                >
                  <Box className="scheduleInfo">
                    <Typography className="courseName" style={{ color: accent.border }}>
                      {s.courseName}
                    </Typography>
                    <Typography className="courseDetails" style={{ color: accent.border }}>
                      {s.courseCode} — {s.location}
                    </Typography>
                    {s.topic && (
                      <Typography className="topicText">
                        Topic: {s.topic}
                      </Typography>
                    )}
                    <Typography className="classSection">
                      Class: {s.className} {s.sectionName}
                    </Typography>
                  </Box>
                  <Box
                    className="timeBox"
                    style={{ backgroundColor: accent.time }}
                  >
                    {timeRange(s.startTime, parseInt(s.durationMins, 10))}
                  </Box>
                </Box>
              );
            })}
          </Box>
        ))
      )}
    </Box>
  );
};

export default ClassScheduleTab;
